/* eslint-disable */
import React from "react";
import { Box } from "@mui/material";
import { StriimTypography } from "@striim/striim-ui";
// @ts-ignore
import { convertNameToHumanRedable } from "app/components/common/editor/control-resource-resolver";
// @ts-ignore
import converter from "core/services/metaStoreService/metaobject-converter";
import SuccessIcon from "src/generic/icon/wizard-icon/successOutlined.svg";
import WarningIcon from "src/generic/icon/customIcons/warning_outline.svg";

const styles = {
    container: { mt: 1 },
    header: {
        display: "flex",
        alignItems: "center",
        gap: 0.5
    },
    icon: {
        display: "flex",
        "& svg": {
            width: 16,
            height: 16,
            fill: "none"
        }
    },
    items: {
        display: "flex",
        flexDirection: "column",
        gap: 0.25,
        mt: 0.5,
        ml: 2.5
    },
    label: {
        color: "greyscale.700",
        fontWeight: 700,
        mr: 0.5
    }
};

const SENSITIVE_KEY = /pass(word)?|secret|token|credential|apikey|api_key|privatekey|access/i;

const humanizeLabel = (key: string): string => convertNameToHumanRedable(key.replace(/[_-]+/g, " ")) ?? key;

// Some form fields hold a { label, value } option object; show only the value.
const displayScalar = (value: unknown): string => {
    if (value === null || value === undefined) return "";
    if (typeof value === "boolean") return value ? "Yes" : "No";
    if (typeof value === "object") {
        const val = (value as Record<string, unknown>).value;
        if (val !== undefined) return displayScalar(val);
        try {
            return JSON.stringify(value);
        } catch {
            return String(value);
        }
    }
    return String(value);
};

const formatValue = (key: string, value: unknown): string => {
    if (SENSITIVE_KEY.test(key)) return "*****";
    if (Array.isArray(value)) return value.length ? value.map(displayScalar).join(", ") : "—";
    if (typeof value === "boolean") return value ? "Yes" : "No";
    if (value === null || value === undefined || value === "") return "—";
    if (typeof value === "object") return displayScalar(value);
    return String(value);
};

const isEmptyValue = (value: unknown): boolean =>
    value === null || value === undefined || value === "" || (Array.isArray(value) && value.length === 0);

// Identifier fields (id, cid, uuid, guid, componentId, appId, …) are internal plumbing —
// hide them from the summary. We tokenize the key the same way as the label so we match
// whole "id"-type words and don't trip on words like "width", "valid" or "hidden".
const ID_WORD = /^(c?id|u?uid|guid)$/i;
const isIdKey = (key: string): boolean =>
    key
        .replace(/[_-]+/g, " ")
        .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
        .split(/\s+/)
        .some(word => ID_WORD.test(word));

const itemsFromRecord = (record: Record<string, unknown> | undefined): SummaryItem[] => {
    if (!record || typeof record !== "object") return [];
    return Object.keys(record)
        .filter(key => !isEmptyValue(record[key]) && !isIdKey(key))
        .map(key => ({ label: humanizeLabel(key), value: formatValue(key, record[key]) }));
};

// Handler names come through as fully-qualified metaobject ids
// (e.g. "Global.PROPERTYTEMPLATE.FileWriter"); use the metaobject converter to
// extract just the adapter/formatter name, stripping namespace, type and version.
const shortName = (name: unknown): string =>
    typeof name === "string" && name ? converter.getNameWithoutVersion(name) : "";

// The base simple source/target forms return a nested model:
//   { name, adapter: { name, properties }, formatter|parser: { name, properties } }
// Flatten it so the adapter and its formatter/parser each contribute one line per
// property, rather than a single JSON blob.
const itemsFromComponentModel = (data: any): SummaryItem[] => {
    if (!data || typeof data !== "object") return [];

    // If it doesn't look like the nested component model, fall back to a flat listing.
    if (!data.adapter && !data.formatter && !data.parser) {
        return itemsFromRecord(data);
    }

    const items: SummaryItem[] = [];

    if (data.adapter) {
        const adapterName = shortName(data.adapter.name);
        if (adapterName) items.push({ label: "Adapter", value: adapterName });
        items.push(...itemsFromRecord(data.adapter.properties));
    }

    const handler = data.formatter ?? data.parser;
    const handlerLabel = data.formatter ? "Formatter" : "Parser";
    if (handler) {
        const handlerName = shortName(handler.name);
        if (handlerName) items.push({ label: handlerLabel, value: handlerName });
        items.push(...itemsFromRecord(handler.properties));
    }

    return items;
};

const CANCEL_STEP_LABELS: Record<string, string> = {
    showEditorForTQL: "TQL review",
    askAppType: "ingestion mode selection",
    checkAppNameAvailability: "name app",
    configureSource: "source configuration",
    showSimpleWizardForm: "source configuration",
    configureTarget: "target configuration",
    showBasicTargetForm: "target configuration",
    askUser: "input collection",
    pickAdapter: "adapter selection"
};

export interface SummaryItem {
    label: string;
    value: string;
}

export interface HitlResultSummary {
    title: string;
    items: SummaryItem[];
    variant?: "success" | "warning";
}

/**
 * Build a structured summary of what a human-in-the-loop tool returned once the
 * interaction is complete. `result` is the raw string passed to the tool's
 * `respond()` callback — either plain text or a JSON payload. `formTitle` is the
 * tool's own call-time args.title (e.g. askUser's form heading) — it lives on the
 * args the tool was invoked with, not on `result`, so callers pass it separately.
 * The returned title is a short sentence; `items` lists every value the user
 * provided, line by line.
 */
export const summarizeHitlResult = (toolName: string, result: string, formTitle?: string): HitlResultSummary => {
    const raw = (result ?? "").trim();

    let parsed: any = null;
    try {
        parsed = JSON.parse(raw);
    } catch {
        parsed = null;
    }

    // Cancellation is signalled consistently across tools. confirmUser has its own
    // confirm/cancel wording below, so it's excluded here.
    if (toolName !== "confirmUser" && parsed && (parsed.cancelled === true || parsed.status === "cancelled")) {
        const stepLabel = CANCEL_STEP_LABELS[toolName];
        return {
            title: stepLabel ? `You cancelled the ${stepLabel} step.` : "You cancelled this step.",
            items: [],
            variant: "warning"
        };
    }

    if (parsed && (parsed.status === "source-disabled" || parsed.status === "target-disabled")) {
        const kind = parsed.status === "source-disabled" ? "source" : "target";
        return {
            title: `This ${kind} adapter isn't enabled for your license.`,
            items: [],
            variant: "warning"
        };
    }

    if (parsed && parsed.status === "adapter-not-found") {
        return {
            title: parsed.adapterName
                ? `The "${parsed.adapterName}" adapter could not be found.`
                : "The requested adapter could not be found.",
            items: [],
            variant: "warning"
        };
    }

    switch (toolName) {
        case "askAppType":
            return {
                title: "You chose how to read the data.",
                items: raw ? [{ label: "Ingestion mode", value: raw }] : []
            };

        case "checkAppNameAvailability":
            return {
                title: "You confirmed the application name.",
                items: [
                    { label: "Name", value: formatValue("name", parsed?.name) },
                    { label: "Namespace", value: formatValue("nsName", parsed?.nsName) },
                    { label: "Fully qualified name", value: formatValue("fqn", parsed?.fullyQualifiedName) }
                ].filter(item => item.value !== "—")
            };

        case "configureSource": {
            if (parsed?.status === "Source configured") {
                const items: SummaryItem[] = [];
                if (parsed.sourceType) items.push({ label: "Source type", value: String(parsed.sourceType) });
                if (parsed.mode) items.push({ label: "Ingestion mode", value: String(parsed.mode) });
                items.push(...itemsFromRecord(parsed.connectionParams));
                if (Array.isArray(parsed.selectedTables) && parsed.selectedTables.length) {
                    items.push({ label: "Tables", value: parsed.selectedTables.join(", ") });
                }
                return { title: "You configured the source.", items };
            }
            if (parsed?.status === "automated-not-supported") {
                return { title: "Automated pipeline isn't supported for this source.", items: [], variant: "warning" };
            }
            if (parsed?.status === "unknown-source-type") {
                return {
                    title: "The source type wasn't recognised.",
                    items: parsed.sourceType ? [{ label: "Source type", value: String(parsed.sourceType) }] : [],
                    variant: "warning"
                };
            }
            return { title: "Source configuration complete.", items: [] };
        }

        case "showSimpleWizardForm":
            return { title: "You configured the source.", items: itemsFromComponentModel(parsed?.properties) };

        case "configureTarget": {
            if (parsed?.status === "configured") {
                const items: SummaryItem[] = [];
                if (parsed.targetType) items.push({ label: "Target type", value: String(parsed.targetType) });
                items.push(...itemsFromRecord(parsed.formValues));
                return { title: "You configured the target.", items };
            }
            if (parsed?.status === "unknown-target-type") {
                return {
                    title: "The target type wasn't recognised.",
                    items: parsed.targetType ? [{ label: "Target type", value: String(parsed.targetType) }] : [],
                    variant: "warning"
                };
            }
            return { title: "Target configuration complete.", items: [] };
        }

        case "showBasicTargetForm":
            return { title: "You configured the target.", items: itemsFromComponentModel(parsed?.properties) };

        case "confirmUser":
            return {
                title: parsed?.confirmed ? "You confirmed the action." : "You cancelled the action.",
                items: [],
                variant: parsed?.confirmed ? "success" : "warning"
            };

        case "askUser": {
            const trimmedFormTitle = (formTitle ?? "").trim();
            const submittedTitle = trimmedFormTitle
                ? `You submitted the ${trimmedFormTitle} form.`
                : "You submitted the form.";
            const providedDetailsTitle = trimmedFormTitle
                ? `You provided the requested details for ${trimmedFormTitle}.`
                : "You provided the requested details.";
            if (parsed?.values && typeof parsed.values === "object") {
                const items = itemsFromRecord(parsed.values);
                return {
                    title: items.length ? providedDetailsTitle : submittedTitle,
                    items
                };
            }
            return { title: submittedTitle, items: [] };
        }

        case "showEditorForTQL": {
            if (raw.includes("Navigate")) return { title: "Opened the app in the Flow Designer.", items: [] };
            if (raw.includes("App is updated")) return { title: "You applied the TQL changes.", items: [] };
            return { title: "You reviewed the TQL.", items: [] };
        }

        case "pickAdapter": {
            if (parsed?.status === "selected") {
                return {
                    title: "You selected an adapter.",
                    items: [
                        { label: "Type", value: formatValue("type", parsed.type) },
                        { label: "Adapter", value: formatValue("value", parsed.value) }
                    ].filter(item => item.value !== "—")
                };
            }
            if (parsed?.status === "missing-source") {
                return {
                    title: "The app doesn't have a source to attach a target to yet.",
                    items: [],
                    variant: "warning"
                };
            }
            return { title: "Step completed.", items: [] };
        }

        default:
            return { title: "Step completed.", items: [] };
    }
};

export interface HitlSummaryProps {
    toolName: string;
    result: string;
    // The tool call's own args.title (e.g. askUser's form heading), when it has one —
    // this isn't part of `result`, so pass it through from the tool's render args.
    title?: string;
}

/**
 * Read-only summary rendered in the chat thread after a human-in-the-loop tool
 * completes, so the conversation keeps a record of everything the user configured.
 * Shows a short sentence followed by each submitted value, line by line.
 */
export const HitlSummary: React.FC<HitlSummaryProps> = ({ toolName, result, title: formTitle }) => {
    const { title, items, variant = "success" } = summarizeHitlResult(toolName, result, formTitle);
    return (
        <Box sx={styles.container}>
            <Box sx={styles.header}>
                <Box sx={styles.icon}>{variant === "warning" ? <WarningIcon /> : <SuccessIcon />}</Box>
                <StriimTypography variant="body4" color="greyscale.700">
                    {title}
                </StriimTypography>
            </Box>
            {items.length > 0 && (
                <Box sx={styles.items}>
                    {items.map(item => (
                        <StriimTypography key={item.label} variant="body4" color="greyscale.800">
                            <Box component="span" sx={styles.label}>
                                {item.label}:
                            </Box>
                            <code> {item.value}</code>
                        </StriimTypography>
                    ))}
                </Box>
            )}
        </Box>
    );
};

export default HitlSummary;
